import { activeFrameIntervalMs, idleGestureDurationMs, idleScheduleMs } from './timing'

export type LauncherGesture = keyof typeof idleGestureDurationMs

const durations = idleGestureDurationMs

const priority: Record<LauncherGesture, number> = {
  blink: 0,
  glance: 0,
  peek: 0,
  hover: 1,
  complete: 2,
}

export class LauncherMotion {
  private listeners = new Set<() => void>()
  private gesture: LauncherGesture | null = null
  private started = 0
  private deadlines = { blink: 0, glance: 0 }
  private paused = true
  private typing = false
  readonly frame = { blink: 0, gaze: 0, tilt: 0, lower: 0, light: 0, delighted: false }

  constructor(
    private readonly clock = () => performance.now(),
    private readonly random = Math.random,
  ) {
    this.schedule()
  }

  private schedule() {
    const now = this.clock()
    this.deadlines = {
      blink: now + idleScheduleMs.blink.base + this.random() * idleScheduleMs.blink.jitter,
      glance: now + idleScheduleMs.glance.base + this.random() * idleScheduleMs.glance.jitter,
    }
  }

  subscribe(listener: () => void) {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private notify() {
    this.listeners.forEach(listener => listener())
  }

  setPaused(paused: boolean) {
    if (paused === this.paused) {
      return
    }
    this.paused = paused
    this.gesture = null
    this.schedule()
    this.resetFrame()
    this.notify()
  }

  setTyping(typing: boolean) {
    this.typing = typing
    if (typing && (this.gesture === 'peek' || this.gesture === 'glance')) {
      this.gesture = null
      this.resetFrame()
      this.notify()
    }
  }

  request(gesture: LauncherGesture) {
    if (this.paused) {
      return
    }
    if (
      this.gesture &&
      this.clock() - this.started < durations[this.gesture] &&
      priority[this.gesture] >= priority[gesture]
    ) {
      return
    }
    this.gesture = gesture
    this.started = this.clock()
    this.notify()
  }

  private resetFrame() {
    Object.assign(this.frame, { blink: 0, gaze: 0, tilt: 0, lower: 0, light: 0, delighted: false })
  }

  sample() {
    this.resetFrame()
    if (this.paused) {
      return this.frame
    }
    const now = this.clock()
    if (this.gesture && now - this.started >= durations[this.gesture]) {
      this.gesture = null
    }
    for (const id of ['glance', 'blink'] as const) {
      if (now >= this.deadlines[id]) {
        const schedule = idleScheduleMs[id]
        this.deadlines[id] = now + schedule.base + this.random() * schedule.jitter
        if (!this.gesture && (!this.typing || id === 'blink')) {
          this.gesture = id
          this.started = now
        }
      }
    }
    if (!this.gesture) {
      return this.frame
    }
    const progress = Math.min(1, (now - this.started) / durations[this.gesture])
    const pulse = Math.sin(progress * Math.PI) ** 2
    switch (this.gesture) {
      case 'blink': {
        this.frame.blink = pulse
        break
      }
      case 'glance':
      case 'hover': {
        this.frame.gaze = pulse
        this.frame.tilt = pulse * -0.12
        break
      }
      case 'peek': {
        this.frame.lower =
          progress < 0.5
            ? Math.sin(progress * Math.PI * 2) ** 2 * 1.05
            : -(Math.sin((progress - 0.5) * Math.PI * 2) ** 2) * 0.14
        this.frame.tilt = progress > 0.5 ? pulse * -0.18 : 0
        break
      }
      case 'complete': {
        this.frame.delighted = progress < 0.85
        this.frame.light = pulse
        this.frame.tilt = Math.sin(progress * Math.PI * 4) * pulse * 0.07
        break
      }
    }
    return this.frame
  }

  get active() {
    return !this.paused && this.gesture !== null
  }
  get suspended() {
    return this.paused
  }
  get nextDelay() {
    return this.paused
      ? Infinity
      : this.active
        ? activeFrameIntervalMs
        : Math.max(0, Math.min(...Object.values(this.deadlines)) - this.clock())
  }
}
